import React from "react";
import { Star, Download, Printer, Share2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { resolveImageUrl } from "../../utils/imageUrl";
import { toast } from "sonner";

function StarRating({ rating, onChange, size = 20 }) {
    const [hovered, setHovered] = React.useState(0);
    return (
        <div className="flex gap-0.5">
            {[1,2,3,4,5].map(n => (
                <button key={n} type="button"
                    onClick={() => onChange && onChange(n)}
                    onMouseEnter={() => onChange && setHovered(n)}
                    onMouseLeave={() => onChange && setHovered(0)}
                    className={onChange ? "cursor-pointer" : "cursor-default"}>
                    <Star size={size}
                        className={(hovered || rating) >= n ? "text-yellow-400 fill-yellow-400" : "text-gray-200 fill-gray-200"}
                    />
                </button>
            ))}
        </div>
    );
}

export function ProductTabs({
    product, activeTab, setActiveTab, brandDescription, brandLogo,
    reviews, reviewsTotal, reviewsAvg,
    addingReview, setAddingReview, reviewForm, setReviewForm,
    submittingReview, handleSubmitReview,
}) {
    return (
        <div className="mt-12" id="recenze">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="mb-6">
                    <TabsTrigger value="description">Popis</TabsTrigger>
                    <TabsTrigger value="params">Parametry</TabsTrigger>
                    {product.documents && product.documents.length > 0 && (
                        <TabsTrigger value="documents">
                            Dokumenty <span className="ml-1.5 bg-gray-100 text-gray-600 text-xs px-1.5 py-0.5 rounded-full">{product.documents.length}</span>
                        </TabsTrigger>
                    )}
                    <TabsTrigger value="reviews">
                        Recenze {reviewsTotal > 0 && <span className="ml-1.5 bg-gray-100 text-gray-600 text-xs px-1.5 py-0.5 rounded-full">{reviewsTotal}</span>}
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="description">
                    <div className="prose max-w-none text-gray-600 leading-relaxed bg-white rounded-xl border border-gray-100 p-6">
                        {product.description ? (
                            <div dangerouslySetInnerHTML={{ __html: product.description.replace(/\n/g, "<br/>") }} />
                        ) : <p>Popis neni k dispozici.</p>}
                        {brandDescription && product.brand && (
                            <div className="mt-8 pt-6 border-t border-gray-200" data-testid="brand-description">
                                <div className="flex items-center gap-3 mb-3">
                                    {brandLogo && <img src={resolveImageUrl(brandLogo)} alt={product.brand} className="h-8 object-contain" />}
                                    <h3 className="text-lg font-semibold text-gray-800">O znacce {product.brand}</h3>
                                </div>
                                <div className="text-gray-600 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: brandDescription }} />
                            </div>
                        )}
                    </div>
                </TabsContent>

                <TabsContent value="params">
                    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden" data-testid="product-params-tab">
                        {product.parameters && (Array.isArray(product.parameters) ? product.parameters.length > 0 : Object.keys(product.parameters).length > 0) ? (
                            <table className="w-full">
                                <tbody>
                                    {Object.entries(product.parameters).map(([key, val], i) => (
                                        <tr key={key} className={i % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                                            <td className="px-6 py-3 text-sm font-medium text-gray-600 w-1/2 border-b border-gray-100">{key}</td>
                                            <td className="px-6 py-3 text-sm text-gray-800 border-b border-gray-100 font-medium">{String(val)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : <p className="p-6 text-gray-400 text-sm">Parametry nejsou k dispozici.</p>}
                    </div>
                </TabsContent>

                {product.documents && product.documents.length > 0 && (
                    <TabsContent value="documents">
                        <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="product-documents-tab">
                            <div className="space-y-2">
                                {product.documents.map((doc, i) => (
                                    <a key={i} href={doc.url} target="_blank" rel="noopener noreferrer"
                                        className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-50 hover:bg-blue-50 border border-gray-100 hover:border-blue-200 transition-colors group"
                                        data-testid={`document-download-${i}`}>
                                        <span className="w-9 h-9 rounded-lg bg-red-100 text-red-600 flex items-center justify-center text-[10px] font-bold uppercase flex-shrink-0">
                                            {doc.type || "PDF"}
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-700 group-hover:text-blue-700 truncate">{doc.name || "Dokument"}</p>
                                            {doc.size && <p className="text-[10px] text-gray-400">{doc.size}</p>}
                                        </div>
                                        <Download size={16} className="text-gray-400 group-hover:text-blue-600 flex-shrink-0" />
                                    </a>
                                ))}
                            </div>
                        </div>
                    </TabsContent>
                )}

                <TabsContent value="reviews">
                    <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="product-reviews-tab">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                                <StarRating rating={Math.round(reviewsAvg)} size={18} />
                                <span className="text-lg font-bold">{reviewsAvg}</span>
                                <span className="text-sm text-gray-400">({reviewsTotal} recenzi)</span>
                            </div>
                            {!addingReview && (
                                <Button size="sm" variant="outline" onClick={() => setAddingReview(true)} data-testid="add-review-btn">
                                    Napsat recenzi
                                </Button>
                            )}
                        </div>
                        {addingReview && (
                            <form onSubmit={handleSubmitReview} className="mb-6 p-4 rounded-xl bg-gray-50 border border-gray-100">
                                <div className="grid gap-3">
                                    <div>
                                        <Label className="text-xs">Vase jmeno *</Label>
                                        <Input value={reviewForm.author_name} onChange={e => setReviewForm(f => ({...f, author_name: e.target.value}))}
                                            placeholder="Jan Novak" className="mt-1" data-testid="review-author-input" />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Hodnoceni</Label>
                                        <StarRating rating={reviewForm.rating} onChange={r => setReviewForm(f => ({...f, rating: r}))} size={24} />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Nadpis</Label>
                                        <Input value={reviewForm.title} onChange={e => setReviewForm(f => ({...f, title: e.target.value}))}
                                            placeholder="Skvely produkt" className="mt-1" data-testid="review-title-input" />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Recenze *</Label>
                                        <textarea value={reviewForm.body} onChange={e => setReviewForm(f => ({...f, body: e.target.value}))}
                                            placeholder="Vase zkusenosti s produktem..."
                                            className="mt-1 flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                            data-testid="review-body-input" />
                                    </div>
                                    <div className="flex gap-2">
                                        <Button type="submit" disabled={submittingReview} size="sm" data-testid="review-submit-btn">
                                            {submittingReview ? "Odesilam..." : "Odeslat recenzi"}
                                        </Button>
                                        <Button type="button" variant="ghost" size="sm" onClick={() => setAddingReview(false)}>Zrusit</Button>
                                    </div>
                                </div>
                            </form>
                        )}
                        {reviews.length === 0 ? (
                            <p className="text-gray-400 text-sm">Zatim zadne recenze. Budte prvni!</p>
                        ) : (
                            <div className="space-y-4">
                                {reviews.map((rev) => (
                                    <div key={rev.id || rev.created_at} className="border-b border-gray-100 pb-4 last:border-0">
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                                                style={{ backgroundColor: "var(--sg-primary)" }}>
                                                {rev.author_name?.charAt(0).toUpperCase()}
                                            </div>
                                            <div className="flex-1">
                                                <p className="font-semibold text-gray-800 text-sm">{rev.author_name}</p>
                                                <p className="text-xs text-gray-400">
                                                    {new Date(rev.created_at).toLocaleDateString("cs-CZ")}
                                                </p>
                                            </div>
                                            <StarRating rating={rev.rating} size={14} />
                                        </div>
                                        {rev.title && <p className="font-semibold text-gray-800 text-sm mb-1">{rev.title}</p>}
                                        <p className="text-sm text-gray-600">{rev.body}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}

export function ProductShareStrip({ product }) {
    return (
        <div className="flex items-center gap-2 pt-2 border-t border-gray-100" data-testid="product-share-strip">
            <button onClick={() => window.print()}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-gray-500 text-xs hover:bg-gray-50 transition-colors"
                data-testid="product-print-btn">
                <Printer size={12} /> Tisknout
            </button>
            <button onClick={() => {
                const url = window.location.href;
                if (navigator.share) { navigator.share({ title: product.name, url }); }
                else { navigator.clipboard.writeText(url); toast.success("Odkaz zkopirovan"); }
            }}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-gray-500 text-xs hover:bg-gray-50 transition-colors"
                data-testid="product-share-btn">
                <Share2 size={12} /> Sdilet
            </button>
        </div>
    );
}

export function ProductTrustIcons({ product }) {
    return (
        <div className="mt-10 grid grid-cols-3 gap-4 bg-gray-50 rounded-2xl p-6">
            {[
                { icon: "Truck", label: "Doprava", desc: "PPL od 199 Kc" },
                { icon: "Shield", label: "Zaruka", desc: `${product.warranty_months >= 12 ? Math.floor(product.warranty_months / 12) + " let" : (product.warranty_months || 24) + " mes."}` },
                { icon: "RotateCcw", label: "Vraceni", desc: "30 dnu" },
            ].map(item => (
                <div key={item.label} className="text-center">
                    <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mx-auto mb-2 shadow-sm border border-gray-100">
                        <span className="text-gray-500 text-lg font-bold">{item.icon.charAt(0)}</span>
                    </div>
                    <p className="text-sm font-bold text-gray-800">{item.label}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
                </div>
            ))}
        </div>
    );
}
